/*
 * SyncRulesPanel — shared catalog-sync rules UI for both buyer + seller
 * dropship pages. Renders an "Active rules" list + a create/edit dialog.
 *
 * Rules ALWAYS:
 *   • Skip OOS (stock = 0)        — locked default per product spec
 *   • Skip drafts                  — locked default per product spec
 *   • Add new only (never overwrite manual selections)
 *
 * Rules are evaluated immediately on save and re-evaluated automatically
 * when new products land. The per-rule `last_apply_count` shows how many
 * products the rule moved the last time it ran.
 *
 * `role`         — "seller" | "buyer"
 * `categoryOptions` — [{value, label}] derived from the current product list
 * `supplierOptions` — [{value, label}] (buyer only)
 * `onChange`     — callback fired after any rule mutation so the parent
 *                  can refetch products / show updated counts
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Sparkles, Filter as FilterIcon, X, Trash2, Pencil,
  PackageX, EyeOff, Shield, RefreshCw, Loader2, Check,
} from "lucide-react";
import api from "../../lib/api";

const LOCKED_DEFAULTS = [
  { icon: PackageX, label: "Skip out-of-stock",     hint: "Items with 0 stock are never synced." },
  { icon: EyeOff,   label: "Skip drafts",           hint: "Only sync products that are live." },
  { icon: Shield,   label: "Add new only",          hint: "Never overwrites manual catalog choices." },
];

export default function SyncRulesPanel({ role, categoryOptions = [], supplierOptions = [], onChange }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null=closed, {} = new, {...rule}=edit

  const load = useCallback(() => {
    setLoading(true);
    api.get("/dropship/sync-rules")
      .then(r => setRules(r.data || []))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRule = async (draft) => {
    try {
      if (draft.id) {
        await api.patch(`/dropship/sync-rules/${draft.id}`, {
          name: draft.name,
          filters: draft.filters,
          active: draft.active,
        });
        toast.success("Rule updated");
      } else {
        await api.post("/dropship/sync-rules", {
          name: draft.name,
          filters: draft.filters,
          active: draft.active ?? true,
        });
        toast.success("Rule created · auto-syncing matching products");
      }
      setEditing(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/dropship/sync-rules/${rule.id}`, { active: !rule.active });
      load();
      onChange?.();
    } catch { toast.error("Update failed"); }
  };

  const runNow = async (rule) => {
    try {
      const { data } = await api.post(`/dropship/sync-rules/${rule.id}/run`);
      toast.success(`${rule.name} · ${data.applied} new product${data.applied === 1 ? "" : "s"} synced`);
      load();
      onChange?.();
    } catch { toast.error("Re-run failed"); }
  };

  const deleteRule = async (rule) => {
    if (!confirm(`Delete rule "${rule.name}"? Already-synced products stay in your catalog.`)) return;
    try {
      await api.delete(`/dropship/sync-rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
      onChange?.();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white" data-testid="sync-rules-panel">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-700"/>
            <div className="font-medium">Auto-sync rules</div>
          </div>
          <div className="text-[12px] text-foreground/55 mt-0.5">
            Save a filter once → it auto-syncs matching products forever, including any new ones you add later.
          </div>
        </div>
        <Button onClick={() => setEditing({})} className="rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90"
          data-testid="new-sync-rule-btn">
          <Plus className="w-4 h-4 mr-1.5"/>New rule
        </Button>
      </div>

      {/* Locked-default badges — explains the engine's safe behavior */}
      <div className="px-5 py-2.5 bg-secondary/30 border-b border-border flex items-center gap-1.5 flex-wrap text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/60">
        {LOCKED_DEFAULTS.map(d => (
          <span key={d.label} title={d.hint} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-border">
            <d.icon className="w-3 h-3"/>{d.label}
          </span>
        ))}
      </div>

      {loading ? (
        <div className="px-5 py-10 text-center text-foreground/40">
          <Loader2 className="w-4 h-4 mx-auto animate-spin"/>
        </div>
      ) : rules.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <div className="w-10 h-10 rounded-2xl bg-secondary grid place-items-center mx-auto">
            <FilterIcon className="w-4 h-4 text-foreground/45"/>
          </div>
          <div className="mt-3 text-[13px] text-foreground/65">No sync rules yet</div>
          <div className="text-[11.5px] text-foreground/45 mt-0.5">
            Create one to auto-sync products matching a filter — categories, price range{role === "buyer" ? ", supplier" : ""}.
          </div>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {rules.map(r => <RuleRow key={r.id} rule={r}
            onToggle={() => toggleActive(r)}
            onRun={() => runNow(r)}
            onEdit={() => setEditing(r)}
            onDelete={() => deleteRule(r)}/>)}
        </div>
      )}

      {editing != null && (
        <RuleDialog
          rule={editing}
          role={role}
          categoryOptions={categoryOptions}
          supplierOptions={supplierOptions}
          onCancel={() => setEditing(null)}
          onSave={saveRule}/>
      )}
    </div>
  );
}

// ─────────────── Rule Row ───────────────
function RuleRow({ rule, onToggle, onRun, onEdit, onDelete }) {
  const f = rule.filters || {};
  const summary = [];
  if (f.categories?.length) summary.push(`${f.categories.length} categor${f.categories.length === 1 ? "y" : "ies"}`);
  if (f.price_min != null || f.price_max != null) {
    const lo = f.price_min != null ? `$${f.price_min}` : "$0";
    const hi = f.price_max != null ? `$${f.price_max}` : "∞";
    summary.push(`${lo}–${hi}`);
  }
  if (f.seller_ids?.length) summary.push(`${f.seller_ids.length} supplier${f.seller_ids.length === 1 ? "" : "s"}`);
  const summaryText = summary.length ? summary.join(" · ") : "No filters · matches everything eligible";

  return (
    <div className="px-5 py-3 flex items-center gap-3 hover:bg-secondary/20" data-testid={`sync-rule-${rule.id}`}>
      <Switch checked={!!rule.active} onCheckedChange={onToggle} data-testid={`toggle-rule-${rule.id}`}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-medium truncate">{rule.name}</div>
          {!rule.active && <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-foreground/8 text-foreground/55">Paused</span>}
        </div>
        <div className="text-[11.5px] text-foreground/55 truncate mt-0.5">{summaryText}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[12.5px] font-medium">
          {rule.last_apply_count || 0} <span className="text-foreground/55 font-normal">synced</span>
        </div>
        {rule.last_run_at && (
          <div className="text-[10px] text-foreground/45">last run {timeAgo(rule.last_run_at)}</div>
        )}
      </div>
      <div className="flex items-center gap-0.5">
        <button onClick={onRun} title="Re-run now" data-testid={`run-rule-${rule.id}`}
          className="rounded-full h-8 w-8 grid place-items-center text-foreground/55 hover:text-foreground hover:bg-secondary">
          <RefreshCw className="w-3.5 h-3.5"/>
        </button>
        <button onClick={onEdit} title="Edit" data-testid={`edit-rule-${rule.id}`}
          className="rounded-full h-8 w-8 grid place-items-center text-foreground/55 hover:text-foreground hover:bg-secondary">
          <Pencil className="w-3.5 h-3.5"/>
        </button>
        <button onClick={onDelete} title="Delete" data-testid={`delete-rule-${rule.id}`}
          className="rounded-full h-8 w-8 grid place-items-center text-foreground/55 hover:text-rose-700 hover:bg-rose-50">
          <Trash2 className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
}

// ─────────────── Rule Dialog ───────────────
function RuleDialog({ rule, role, categoryOptions, supplierOptions, onCancel, onSave }) {
  const isEdit = !!rule.id;
  const [name, setName] = useState(rule.name || "");
  const [categories, setCategories] = useState(rule.filters?.categories || []);
  const [priceMin, setPriceMin] = useState(rule.filters?.price_min ?? "");
  const [priceMax, setPriceMax] = useState(rule.filters?.price_max ?? "");
  const [sellerIds, setSellerIds] = useState(rule.filters?.seller_ids || []);
  const [active, setActive] = useState(rule.active ?? true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const filters = useMemo(() => ({
    categories: categories.length ? categories : undefined,
    price_min: priceMin === "" ? undefined : Number(priceMin),
    price_max: priceMax === "" ? undefined : Number(priceMax),
    seller_ids: role === "buyer" && sellerIds.length ? sellerIds : undefined,
  }), [categories, priceMin, priceMax, sellerIds, role]);

  // Live preview — debounced ~300ms
  useEffect(() => {
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const { data } = await api.post("/dropship/sync-rules/preview", { filters });
        setPreview(data);
      } catch { setPreview(null); }
      finally { setPreviewing(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name your rule (e.g. \"Beauty under $30\")");
    setSaving(true);
    try {
      await onSave({ ...rule, name: name.trim(), filters, active });
    } finally { setSaving(false); }
  };

  const toggleCat = (v) => setCategories(cs =>
    cs.includes(v) ? cs.filter(x => x !== v) : [...cs, v]);
  const toggleSeller = (v) => setSellerIds(ss =>
    ss.includes(v) ? ss.filter(x => x !== v) : [...ss, v]);

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="sync-rule-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit sync rule" : "New sync rule"}</DialogTitle>
          <DialogDescription>
            Save a filter once. We'll auto-sync any matching product, including ones you add later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Rule name</Label>
            <Input value={name} onChange={e => setName(e.target.value)}
              placeholder='e.g. "Beauty under $30" or "Holiday SKUs"'
              data-testid="rule-name" autoFocus/>
          </div>

          {/* Categories */}
          {categoryOptions.length > 0 && (
            <div>
              <Label>Categories</Label>
              <div className="text-[11px] text-foreground/55 mb-1.5">Pick one or more · leave empty for all</div>
              <div className="flex flex-wrap gap-1.5">
                {categoryOptions.map(o => {
                  const on = categories.includes(o.value);
                  return (
                    <button key={o.value} type="button" onClick={() => toggleCat(o.value)}
                      data-testid={`cat-chip-${o.value}`}
                      className={`inline-flex items-center gap-1 rounded-full h-7 px-3 text-[12px] transition-colors ${
                        on ? "bg-foreground text-background" : "bg-white border border-border hover:bg-secondary"
                      }`}>
                      {on && <Check className="w-3 h-3"/>}
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price range */}
          <div>
            <Label>Price range</Label>
            <div className="text-[11px] text-foreground/55 mb-1.5">{role === "seller" ? "Wholesale price" : "Wholesale cost from supplier"}</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55 text-[12px]">$</span>
                <Input type="number" min="0" step="0.01"
                  value={priceMin} onChange={e => setPriceMin(e.target.value)}
                  placeholder="Min" className="pl-7" data-testid="price-min"/>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55 text-[12px]">$</span>
                <Input type="number" min="0" step="0.01"
                  value={priceMax} onChange={e => setPriceMax(e.target.value)}
                  placeholder="Max" className="pl-7" data-testid="price-max"/>
              </div>
            </div>
          </div>

          {/* Suppliers (buyer only) */}
          {role === "buyer" && supplierOptions.length > 0 && (
            <div>
              <Label>Suppliers</Label>
              <div className="text-[11px] text-foreground/55 mb-1.5">Restrict to specific suppliers · leave empty for all</div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {supplierOptions.map(o => {
                  const on = sellerIds.includes(o.value);
                  return (
                    <button key={o.value} type="button" onClick={() => toggleSeller(o.value)}
                      className={`inline-flex items-center gap-1 rounded-full h-7 px-3 text-[12px] transition-colors ${
                        on ? "bg-foreground text-background" : "bg-white border border-border hover:bg-secondary"
                      }`}>
                      {on && <Check className="w-3 h-3"/>}
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Live preview */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-emerald-800 mb-1">Preview</div>
            {previewing ? (
              <div className="text-[12.5px] text-foreground/55 inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin"/>Counting…
              </div>
            ) : preview ? (
              <div className="text-[13px]">
                <span className="font-medium">{preview.would_apply}</span>
                <span className="text-foreground/65"> products will be synced now</span>
                {preview.already_synced > 0 && (
                  <span className="text-foreground/55 ml-1">· {preview.already_synced} already synced</span>
                )}
              </div>
            ) : (
              <div className="text-[12.5px] text-foreground/55">—</div>
            )}
          </div>

          {/* Active toggle (for edit only — new rules default active) */}
          {isEdit && (
            <div className="flex items-center justify-between rounded-xl border border-border px-4 py-2.5">
              <div>
                <div className="text-[12.5px] font-medium">Active</div>
                <div className="text-[11px] text-foreground/55">Pausing keeps the rule but stops auto-sync</div>
              </div>
              <Switch checked={active} onCheckedChange={setActive}/>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border">
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !name.trim()}
            data-testid="save-rule-btn"
            className="bg-foreground text-background hover:bg-foreground/90">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            {isEdit ? "Save changes" : "Create & sync now"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Helpers ───────────────
function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const sec = Math.floor((Date.now() - then) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec/3600)}h ago`;
  return `${Math.floor(sec/86400)}d ago`;
}
